import React, { useState, useMemo } from 'react';
import ReactTable from "react-table";
import "react-table/react-table.css";

const SeccionComparativoDetallado = ({ data, anioActivo, mesActivo, loading, infoFiltros }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const anioAct = parseInt(anioActivo) || new Date().getFullYear();
    const anioAnt = anioAct - 1;

    // Filtered data based on search term
    const filteredData = useMemo(() => {
        if (!data) return [];
        if (!searchTerm.trim()) return data;
        const term = searchTerm.toLowerCase();
        return data.filter(item =>
            (item.producto && item.producto.toLowerCase().includes(term)) ||
            (item.presentacion && item.presentacion.toLowerCase().includes(term))
        );
    }, [data, searchTerm]);

    // Calculate KPIs
    const kpis = useMemo(() => {
        return filteredData.reduce((acc, curr) => {
            acc.unidadesAnt += parseFloat(curr.unidades_ant || 0);
            acc.unidadesAct += parseFloat(curr.unidades_act || 0);
            acc.montoAnt += parseFloat(curr.monto_ant || 0);
            acc.montoAct += parseFloat(curr.monto_act || 0);
            return acc;
        }, { unidadesAnt: 0, unidadesAct: 0, montoAnt: 0, montoAct: 0 });
    }, [filteredData]);

    const formatMoney = (value) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value);
    const formatNumber = (value) => new Intl.NumberFormat('es-PE').format(value);

    // Map month logic
    const monthNames = {
        '1': 'Ene', '2': 'Feb', '3': 'Mar', '4': 'Abr', '5': 'May', '6': 'Jun',
        '7': 'Jul', '8': 'Ago', '9': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dic'
    };

    const activeMonthsDisplay = useMemo(() => {
        if (!mesActivo) return 'Todos los meses';
        const selected = mesActivo.split(',');
        if (selected.length === 12) return 'Todos los meses';
        const names = selected.map(m => monthNames[m]).filter(Boolean);
        return names.join(', ');
    }, [mesActivo]);

    return (
        <div className="shadow-sm mb-4" style={{ border: '2px solid #01a59c', borderRadius: '1.25rem' }}>
            <div
                className="card-header border-bottom p-4 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', backgroundColor: '#35605a', borderRadius: '1.25rem' }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div>
                    <h4 className="font-weight-bold text-white m-0">
                        <i className="fas fa-table mr-2 text-white"></i> Comparativo Detallado por Producto
                    </h4>
                    {infoFiltros && <div className="mt-1"><small className="text-white"><i className="fas fa-filter mr-1"></i> Filtrando por: {infoFiltros}</small></div>}
                    <p className="text-white mb-0 mt-1">Análisis profundo a nivel de producto ({data ? data.length : 0} encontrados)</p>
                </div>
                <button className='btn btn-light btn-sm rounded-circle'>
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
                </button>
            </div>

            {isOpen && (
                <div className="card-body position-relative p-4">
                    {loading && (
                        <div className="position-absolute d-flex justify-content-center align-items-center" style={{ top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10 }}>
                            <div className="spinner-border text-primary"></div>
                        </div>
                    )}

                    {/* KPI Cards */}
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3">
                            <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                <div className="card-body p-3 text-center">
                                    <h6 className="text-white text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>Unidades {anioAnt}</h6>
                                    <h4 className="font-weight-bold text-white m-0">{formatNumber(kpis.unidadesAnt)}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                <div className="card-body p-3 text-center">
                                    <h6 className="text-white text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>Unidades {anioAct}</h6>
                                    <h4 className="font-weight-bold text-white m-0">{formatNumber(kpis.unidadesAct)}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                <div className="card-body p-3 text-center">
                                    <h6 className="text-white text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>Monto {anioAnt} (S/.)</h6>
                                    <h4 className="font-weight-bold text-white m-0">{formatMoney(kpis.montoAnt)}</h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 mb-3">
                            <div className="shadow-sm border h-100 rounded" style={{ backgroundColor: '#35605a' }}>
                                <div className="card-body p-3 text-center">
                                    <h6 className="text-white text-uppercase mb-2" style={{ fontSize: '0.75rem' }}>Monto {anioAct} (S/.)</h6>
                                    <h4 className="font-weight-bold text-white m-0">{formatMoney(kpis.montoAct)}</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="form-group mb-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                            <div className="text-muted font-weight-bold mb-2" style={{ fontSize: '0.9rem' }}>
                                <i className="fas fa-calendar-alt mr-2"></i> Meses consultados: <span className="text-primary">{activeMonthsDisplay}</span>
                            </div>
                        </div>
                        <div className="mb-3 d-flex justify-content-end align-items-center">
                            <div className="text-muted font-weight-bold mr-3 mb-0" style={{ fontSize: '0.9rem' }}>
                                <i className="fas fa-boxes mr-2"></i> {filteredData.length} productos encontrados
                            </div>
                            <div className="input-group">
                                <div className="input-group-prepend">
                                    <span className="input-group-text bg-white"><i className="fas fa-search"></i></span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control border-left-0 pl-0"
                                    placeholder="Buscar producto o presentación en la tabla..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-3 rounded shadow-sm">
                        {!filteredData || filteredData.length === 0 ? (
                            <div className="text-center p-4 text-muted">No hay datos para mostrar con los filtros seleccionados.</div>
                        ) : (
                            <ReactTable
                                data={filteredData}
                                columns={[
                                    { Header: "Producto", accessor: "producto", filterable: true },
                                    { Header: "Presentación", accessor: "presentacion", filterable: true },
                                    { Header: `Unidades ${anioAnt}`, accessor: "unidades_ant", filterable: true, Cell: props => <div className="text-right">{props.value}</div> },
                                    { Header: `Unidades ${anioAct}`, accessor: "unidades_act", filterable: true, Cell: props => <div className="text-right">{props.value}</div> },
                                    { Header: `Monto ${anioAnt} (S/.)`, accessor: "monto_ant", filterable: true, Cell: props => <div className="text-right text-muted">{formatMoney(props.value)}</div> },
                                    { Header: `Monto ${anioAct} (S/.)`, accessor: "monto_act", filterable: true, Cell: props => <div className="text-right font-weight-bold text-dark">{formatMoney(props.value)}</div> },
                                    { Header: "% Variación", accessor: "variacion", filterable: true, Cell: props => (<div className="text-center"> <span className={`badge badge-pill ${props.value >= 0 ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '0.85em', padding: '0.4em 0.6em' }}> {props.value > 0 ? '+' : ''}{props.value}% </span> </div>) }
                                ]}
                                defaultPageSize={10}
                                className="-striped -highlight"
                                previousText="Anterior"
                                nextText="Siguiente"
                                loadingText="Cargando..."
                                noDataText="No hay datos"
                                pageText="Página"
                                ofText="de"
                                rowsText="filas"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SeccionComparativoDetallado;
